<main>
  <loading-overlay [isLoading]="isLoading > 0" />
  <p-toast position="bottom-right" key="br"></p-toast>

  <header>
    <div class="headline">
      <h1>
        {{ 'Receipt_Details_Title' | translateWF: 'Receipt' }}
        ({{ typeLabel() | translateWF: typeLabel() }}):
        {{ receipt ? receipt.number : ('Receipt_Details_Title_New' | translateWF: 'New Receipt') }}
      </h1>
      <csl-dropdown [formControl]="status" [allEnumValues]="statusEnum" [enumValueOptions]="statusEnum"></csl-dropdown>
    </div>
    <div class="buttons">
      <p-button [label]="'Save' | translateWF: 'Save'" (click)="save()" [disabled]="!canEdit()" />
      <p-button [label]="'Cancel' | translateWF: 'Cancel'" variant="outlined" (click)="cancel()" />
    </div>
  </header>

  <div class="content">
    <form [formGroup]="receiptForm" class="grid-12">
      <!-- LEFT COLUMN -->
      <div class="span-12-12-9 col">
        @if (!isNewReceipt && !isDraft) {
          <p-message severity="info">{{
            'Receipt_Details_Locked'
              | translateWF
                : 'This receipt is no longer a draft — the document can only change
          status,
          its content is locked.'
          }}</p-message>
        }
        <!-- General Information -->
        <p-panel [header]="'Receipt_Details_General_Information' | translateWF: 'General Information'" [toggleable]="true">
          <p-card>
            <div class="grid-auto-2">
              <div class="form-box">
                <label>{{ 'Receipt_Details_Provider' | translateWF: 'Provider' }}</label>
                <p-autocomplete
                  fluid=""
                  class="w-full"
                  formControlName="provider"
                  [dropdown]="true"
                  [suggestions]="providersToDisplay"
                  optionLabel="name"
                  appendTo="body"
                  (completeMethod)="searchProvider($event)"
                >
                  <ng-template let-business pTemplate="item">
                    <div class="col">
                      <span
                        ><strong>{{ business.code }}</strong> — {{ business.name }}</span
                      >
                    </div>
                  </ng-template>
                </p-autocomplete>
                @if (inputProvider.value; as prov) {
                  <small class="text-color-secondary block mt-1"
                    ><strong>{{ prov.code }}</strong> — {{ prov.name }}</small
                  >
                }
              </div>
              <div class="form-box">
                <label for="issuedDate">{{ 'Receipt_Details_Issued_Date' | translateWF: 'Issued Date' }}</label>
                <p-calendar fluid="" class="w-full" inputId="issuedDate" formControlName="issuedDate" [showIcon]="true" dateFormat="yy-mm-dd"> </p-calendar>
              </div>
              <div class="form-box">
                <label>{{ 'Receipt_Details_Payment_Terms' | translateWF: 'Payment Terms' }}</label>
                <csl-dropdown-master-data class="w-full" [formControl]="paymentTerm" categoryCode="PaymentTermsProvider" [allowCreate]="false" />
              </div>

              @if (isFiscal) {
                <div class="form-box">
                  <label>{{ 'Receipt_Details_Provider_Invoice_Number' | translateWF: 'Provider Invoice Number' }}</label>
                  <input type="text" class="p-inputtext p-component w-full" formControlName="providerInvoiceNumber" />
                </div>
                <div class="form-box">
                  <label>{{ 'Receipt_Details_Fiscal_Invoice_Number' | translateWF: 'Fiscal Invoice Number' }}</label>
                  <input type="text" class="p-inputtext p-component w-full" formControlName="fiscalInvoiceNumber" />
                </div>
              }
            </div>
          </p-card>
        </p-panel>

        <!-- Line Items -->
        <p-panel [header]="'Receipt_Details_Line_Items' | translateWF: 'Line Items'" [toggleable]="true">
          <ng-template pTemplate="icons">
            @if (canEditDocument()) {
              <p-button [label]="'Receipt_Add_Line_Without_Item' | translateWF: 'Add Line Without Item'" icon="pi pi-plus" size="small" variant="outlined" (click)="addLine()" />
            }
          </ng-template>

          <div class="col">
            @if (canEditDocument()) {
              <p-autocomplete
                [dropdown]="true"
                dropdownIcon="pi pi-search"
                [suggestions]="filteredItems"
                field="fieldToDisplay"
                [style]="{ minWidth: '30rem' }"
                [panelStyle]="{ minWidth: 'max-content' }"
                appendTo="body"
                [formControl]="searchItemInput"
                [placeholder]="'Receipt_Search_Item' | translateWF: 'Search item to add a line'"
                (completeMethod)="getItemOptions($event)"
                (onSelect)="onSelectItem($event)"
              />
            }
            <p-card data-variant="compact">
              <ng-container formArrayName="items">
                <p-table [value]="formArrayItems.controls" [responsiveLayout]="'scroll'" styleClass="p-datatable-sm w-full" [tableStyle]="{ 'min-width': '60rem' }">
                  <ng-template pTemplate="header">
                    <tr>
                      <th>{{ 'Receipt_Item' | translateWF: 'Item' }}</th>
                      <th style="width: 14rem">{{ 'Receipt_Item_Classification' | translateWF: 'Classification' }}</th>
                      <th style="width: 8rem">{{ 'Receipt_Item_Quantity' | translateWF: 'Quantity' }}</th>
                      <th style="width: 8rem">{{ 'Receipt_Item_Uom' | translateWF: 'UoM' }}</th>
                      <th style="width: 9rem" class="text-right">
                        {{
                          'Receipt_Item_Unit_Price'
                            | translateWF
                              : 'Unit
                        Price'
                        }}
                      </th>
                      <th style="width: 7rem">{{ 'Receipt_Item_Tax_Rate' | translateWF: 'Tax Rate' }}</th>
                      <th style="width: 9rem" class="text-right">{{ 'Receipt_Item_Total' | translateWF: 'Total' }}</th>
                      @if (canEditDocument()) {
                        <th style="width: 3rem"></th>
                      }
                    </tr>
                  </ng-template>

                  <ng-template pTemplate="body" let-line let-i="rowIndex">
                    <tr [formGroupName]="i">
                      <td>
                        @if (line.get('itemCode')?.value) {
                          <!-- Item from the master: read-only "(code) name" -->
                          <span pTooltip="{{ line.get('description')?.value }}" tooltipPosition="top">
                            <b>({{ line.get('itemCode')?.value }})</b> {{ line.get('description')?.value }}
                          </span>
                        } @else {
                          <!-- Manual line (no item): editable description -->
                          <input
                            type="text"
                            class="p-inputtext p-component w-full"
                            formControlName="description"
                            [placeholder]="'Receipt_Item_Description' | translateWF: 'Description'"
                          />
                        }
                      </td>
                      <td>
                        <csl-dropdown-master-data class="w-full" formControlName="itemClassification" categoryCode="ItemClassification" [allowCreate]="false" />
                      </td>
                      <td>
                        <p-inputnumber styleClass="w-full" [style]="{ width: '100%' }" formControlName="quantity" [min]="0" [minFractionDigits]="2" [maxFractionDigits]="4" />
                      </td>
                      <td>
                        <p-select
                          styleClass="w-full"
                          appendTo="body"
                          [options]="uomOptions"
                          optionLabel="label"
                          optionValue="value"
                          [filter]="true"
                          formControlName="unitOfMeasure"
                        />
                      </td>
                      <td>
                        <p-inputnumber
                          styleClass="w-full"
                          [style]="{ width: '100%' }"
                          inputStyleClass="text-right"
                          formControlName="unitPrice"
                          [min]="0"
                          [minFractionDigits]="2"
                          [maxFractionDigits]="4"
                        />
                      </td>
                      <td>
                        <p-select
                          styleClass="w-full"
                          appendTo="body"
                          [options]="taxRateOptions"
                          optionLabel="label"
                          optionValue="value"
                          [showClear]="true"
                          [placeholder]="'Receipt_Item_Tax_Rate_None' | translateWF: 'None'"
                          formControlName="taxRate"
                        />
                      </td>
                      <td>
                        <p-inputnumber
                          styleClass="w-full"
                          [style]="{ width: '100%' }"
                          inputStyleClass="text-right"
                          [ngModel]="lineTotal(line)"
                          [ngModelOptions]="{ standalone: true }"
                          (ngModelChange)="onLineTotalChange(line, $event)"
                          [min]="0"
                          [minFractionDigits]="2"
                          [maxFractionDigits]="2"
                        />
                      </td>
                      @if (canEditDocument()) {
                        <td class="text-center">
                          <p-button icon="pi pi-trash" severity="danger" variant="text" size="small" (click)="removeLine(i)" />
                        </td>
                      }
                    </tr>
                  </ng-template>

                  <ng-template pTemplate="emptymessage">
                    <tr>
                      <td [attr.colspan]="canEditDocument() ? 8 : 7" class="text-center py-4">{{ 'Receipt_No_Lines' | translateWF: 'No lines added' }}</td>
                    </tr>
                  </ng-template>
                </p-table>
              </ng-container>
            </p-card>
          </div>
        </p-panel>

        <!-- Notes (kept: shared csl-standard-notes component) -->
        <p-panel [header]="'Receipt_Details_Notes' | translateWF: 'Notes'" [toggleable]="true">
          <csl-standard-notes [userLite]="userLite!" [notes]="notes" [viewOnly]="!canEditDocument()" (viewMore)="onViewMore()" />
        </p-panel>

        <!-- Files -->
        @if (receipt?.id) {
          <p-panel [header]="'Receipt_Details_Files' | translateWF: 'Files'" [toggleable]="true">
            <div class="col">
              @if (canEditDocument()) {
                <input type="file" multiple (change)="onFilesSelected($event)" />
              }
              <div class="col-sm">
                @for (file of receipt?.files ?? []; track file.name) {
                  <div class="row-sm-between-center file-row">
                    <a class="cursor-pointer text-primary" (click)="viewFile(file.presignedUrl)">{{ file.name }}</a>
                    @if (canEditDocument()) {
                      <p-button icon="pi pi-trash" severity="danger" [text]="true" (click)="deleteFile(file)" />
                    }
                  </div>
                }
              </div>
            </div>
          </p-panel>
        }
      </div>

      <!-- RIGHT COLUMN -->
      <div class="span-12-12-3 col sticky-summary">
        <!-- Currency & Exchange -->
        <p-panel [header]="'Receipt_Details_Currency_Exchange' | translateWF: 'Currency & Exchange'">
          <p-card>
            <div class="col">
              <div class="form-box">
                <label>{{ 'Receipt_Details_Currency' | translateWF: 'Currency' }}</label>
                <p-select class="w-full" appendTo="body" [options]="currencyOptions" optionLabel="label" optionValue="value" formControlName="currency" />
              </div>
              @if (!isUsd) {
                <div class="form-box">
                  <label>{{ 'Receipt_Details_Exchange_Rate' | translateWF: 'Exchange Rate' }}</label>
                  <p-inputnumber class="w-full" [style]="{ width: '100%' }" formControlName="exchangeRateToUSD" [min]="0" [minFractionDigits]="2" [maxFractionDigits]="6" />
                </div>
              }
            </div>
          </p-card>
        </p-panel>

        <!-- Summary -->
        <p-panel [header]="'Receipt_Details_Summary' | translateWF: 'Summary'">
          <p-card>
            <div class="summary-rows">
              <div class="summary-rows__row">
                <span>{{ 'Receipt_Details_Total_Items' | translateWF: 'Total Items' }}</span>
                <span>{{ formArrayItems.length }}</span>
              </div>
              <div class="summary-rows__row summary-rows__row--total">
                <span>{{ 'Receipt_Details_Total' | translateWF: 'Total' }}</span>
                <span>{{ totalAmount() | number: '1.2-2' }} {{ currencyLabel }}</span>
              </div>
            </div>
          </p-card>
        </p-panel>
      </div>
    </form>
  </div>
</main>
